// Modules
import moment from "moment";

// Interfaces
import {DailyAvailability, WaitlistResult} from "Interfaces/ResortResultInterface";

enum SortType {
  ROOM = "room",
  VIEW = "view",
  POINTS = "points",
  ADJUSTMENTS = "totaladjustedPoints", //MVC-47, adjusted points sorting not working, had to change value, since daily availability call is giving as "totaladjustedPoints": -414
};

const SORT_FIELDS = {
  [SortType.ROOM]: "room",
  [SortType.VIEW]: "viewDescription",
  [SortType.POINTS]: "pointAmount",
  [SortType.ADJUSTMENTS]: "totaladjustedPoints", //MVC-47, adjusted points sorting not working, had to change value, since daily availability call is giving as "totaladjustedPoints": -414
}

enum SortDirection {
  ASC = 'ascending',
  DESC = 'descending',
}

/**
 * Evaluate a sort for two villas based on Room Size ASCENDING
 */
function evalRoomSizeSortAsc(sizeA: number = 0, sizeB: number = 0) {
  const roomSizeCompare = sizeA - sizeB;

  // Room Size sort was successful
  if (roomSizeCompare > 0) {
    return 1;
  } else if (roomSizeCompare < 0){
    return -1;
  }

  // Room Size is identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Room Size DESCENDING
 */
function evalRoomSizeSortDesc(sizeA: number = 0, sizeB: number = 0) {
  const roomSizeCompare = sizeB - sizeA;

  // Room Size sort was successful
  if (roomSizeCompare > 0) {
    return 1;
  } else if (roomSizeCompare < 0){
    return -1;
  }

  // Room Size is identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Points Needed ASCENDING
 */
function evalPointsSortAsc(pointsA: number = 0, pointsB: number = 0) {
  const pointsCompare = pointsA - pointsB;

  // Points Needed sort was successful
  if (pointsCompare > 0) {
    return 1;
  } else if (pointsCompare < 0){
    return -1;
  }

  // Points Needed are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Points Needed DESCENDING
 */
function evalPointsSortDesc(pointsA: number = 0, pointsB: number = 0) {
  const pointsCompare = pointsB - pointsA;

  // Points Needed sort was successful
  if (pointsCompare > 0) {
    return 1;
  } else if (pointsCompare < 0){
    return -1;
  }

  // Points Needed are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Adjustments ASCENDING
 */
function evalAdjustmentsSortAsc(adjustmentsA: number = 0, adjustmentsB: number = 0) {
  const adjustmentsCompare = adjustmentsA - adjustmentsB;

  // Points Needed sort was successful
  if (adjustmentsCompare > 0) {
    return 1;
  } else if (adjustmentsCompare < 0){
    return -1;
  }

  // Adjustments are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Adjustments DESCENDING
 */
function evalAdjustmentsSortDesc(adjustmentsA: number = 0, adjustmentsB: number = 0) {
  const adjustmentsCompare = adjustmentsB - adjustmentsA;

  // Points Needed sort was successful
  if (adjustmentsCompare > 0) {
    return 1;
  } else if (adjustmentsCompare < 0){
    return -1;
  }

  // Adjustments are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Room Type alphabetical ASCENDING
 */
function evalRoomTypeAlphaSortAsc(roomTypeA: string = "", roomTypeB: string = "") {
  const letterCompare = (roomTypeA).localeCompare(roomTypeB);

  // Sort by letters in Room Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Room Types are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on Room Type alphabetical DESCENDING
 */
function evalRoomTypeAlphaSortDesc(roomTypeA: string = "", roomTypeB: string = "") {
  const letterCompare = (roomTypeB).localeCompare(roomTypeA);

  // Sort by letters in Room Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Room Types are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on View alphabetical ASCENDING
 */
function evalViewSortAsc(viewA: string = "", viewB: string = "") {
  const letterCompare = (viewA).localeCompare(viewB);

  // Sort by letters in View was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Views are identical
  return 0;
}

/**
 * Evaluate a sort for two villas based on View alphabetical DESCENDING
 */
function evalViewSortDesc(viewA: string = "", viewB: string = "") {
  const letterCompare = (viewB).localeCompare(viewA);

  // Sort by letters in View was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Views are identical
  return 0;
}

/**
 * Sort function to sort villas based on Room ASCENDING
 *
 * The sort priorities are the following:
 *  1. Size of room ASC (smallest to largest)
 *  2. Room type alphabetical ASC (A-Z)
 *  3. View type alphabetical ASC (A-Z)
 */
function roomSortAscFunc(a: any, b:any, roomsSortOrder:any) {
  // Try to sort by Room Size
  const roomSizeSortResult = evalRoomSizeSortAsc(
    roomsSortOrder[a[SORT_FIELDS[SortType.ROOM]]],
    roomsSortOrder[b[SORT_FIELDS[SortType.ROOM]]],
  );

  // Room Size sort was successful
  if (roomSizeSortResult > 0) {
    return 1;
  } else if (roomSizeSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortAsc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Try to sort by View
  const viewSortResult = evalViewSortAsc(
    a[SORT_FIELDS[SortType.VIEW]],
    b[SORT_FIELDS[SortType.VIEW]]
  );

  // View sort was successful
  if (viewSortResult > 0) {
    return 1;
  } else if (viewSortResult < 0) {
    return -1;
  }

  // Villas's room size, room type, and view type are identical
  return 0;
}

/**
 * Sort function to sort villas based on Room DESCENDING
 *
 * The sort priorities are the following:
 *  1. Size of room DESC (largest to smallest)
 *  2. Room type alphabetical DESC (Z-A)
 *  3. View type alphabetical ASC (A-Z)
 */
function roomSortDescFunc(a: any, b:any, roomsSortOrder:any) {
  // Try to sort by Room Size
  const roomSizeSortResult = evalRoomSizeSortDesc(
    roomsSortOrder[a[SORT_FIELDS[SortType.ROOM]]],
    roomsSortOrder[b[SORT_FIELDS[SortType.ROOM]]],
  );

  // Room Size sort was successful
  if (roomSizeSortResult > 0) {
    return 1;
  } else if (roomSizeSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortDesc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Try to sort by View
  const viewSortResult = evalViewSortAsc(
    a[SORT_FIELDS[SortType.VIEW]],
    b[SORT_FIELDS[SortType.VIEW]]
  );

  // View sort was successful
  if (viewSortResult > 0) {
    return 1;
  } else if (viewSortResult < 0) {
    return -1;
  }

  // Villas's room size, room type, and view type are identical
  return 0;
}

/**
 * Sort function to sort villas based on View ASCENDING
 *
 * The sort priorities are the following:
 *  1. View type alphabetical ASC (A-Z)
 *  2. Points needed ASC (lowest to highest)
 */
function viewSortAscFunc(a: any, b:any) {
  // Try to sort by View
  const viewSortResult = evalViewSortAsc(
    a[SORT_FIELDS[SortType.VIEW]],
    b[SORT_FIELDS[SortType.VIEW]]
  );

  // View sort was successful
  if (viewSortResult > 0) {
    return 1;
  } else if (viewSortResult < 0) {
    return -1;
  }

  // Try to sort by Points Needed
  const pointsSortResult = evalPointsSortAsc(
    a[SORT_FIELDS[SortType.POINTS]],
    b[SORT_FIELDS[SortType.POINTS]]
  );

  // Points Needed sort was successful
  if (pointsSortResult > 0) {
    return 1;
  } else if (pointsSortResult < 0) {
    return -1;
  }

  // Villas's view type and points needed are identical
  return 0;
}

/**
 * Sort function to sort villas based on View DESCENDING
 *
 * The sort priorities are the following:
 *  1. View type alphabetical DESC (Z-A)
 *  2. Points needed ASC (lowest to highest)
 */
function viewSortDescFunc(a: any, b:any) {
  // Try to sort by View
  const viewSortResult = evalViewSortDesc(
    a[SORT_FIELDS[SortType.VIEW]],
    b[SORT_FIELDS[SortType.VIEW]]
  );

  // View sort was successful
  if (viewSortResult > 0) {
    return 1;
  } else if (viewSortResult < 0) {
    return -1;
  }

  // Try to sort by Points Needed
  const pointsSortResult = evalPointsSortAsc(
    a[SORT_FIELDS[SortType.POINTS]],
    b[SORT_FIELDS[SortType.POINTS]]
  );

  // Points Needed sort was successful
  if (pointsSortResult > 0) {
    return 1;
  } else if (pointsSortResult < 0) {
    return -1;
  }

  // Villas's view type and points needed are identical
  return 0;
}

/**
 * Sort function to sort villas based on Points Needed ASCENDING
 *
 * The sort priorities are the following:
 *  1. Points needed ASC (lowest to highest)
 *  2. Room type alphabetical ASC (A-Z)
 */
function pointsSortAscFunc(a: any, b:any) {
  // Try to sort by Points Needed
  const pointsSortResult = evalPointsSortAsc(
    a[SORT_FIELDS[SortType.POINTS]],
    b[SORT_FIELDS[SortType.POINTS]]
  );

  // Points Needed sort was successful
  if (pointsSortResult > 0) {
    return 1;
  } else if (pointsSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortAsc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Villas's points needed and room type are identical
  return 0;
}

/**
 * Sort function to sort villas based on Points Needed DESCENDING
 *
 * The sort priorities are the following:
 *  1. Points needed DESC (highest to lowest)
 *  2. Room type alphabetical ASC (A-Z)
 */
function pointsSortDescFunc(a: any, b:any) {
  // Try to sort by Points Needed
  const pointsSortResult = evalPointsSortDesc(
    a[SORT_FIELDS[SortType.POINTS]],
    b[SORT_FIELDS[SortType.POINTS]]
  );

  // Points Needed sort was successful
  if (pointsSortResult > 0) {
    return 1;
  } else if (pointsSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortAsc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Villas's points needed and room type are identical
  return 0;
}

/**
 * Sort function to sort villas based on Adjustments points cost ASCENDING
 *
 * The sort priorities are the following:
 *  1. Adjustments ASC (lowest to highest)
 *  2. Room type alphabetical ASC (A-Z)
 */
function adjustmentsSortAscFunc(a: any, b:any) {
  // Try to sort by Adjustments
  const adjustmentsSortResult = evalAdjustmentsSortAsc(
    a[SORT_FIELDS[SortType.ADJUSTMENTS]],
    b[SORT_FIELDS[SortType.ADJUSTMENTS]]
  );

  // Adjustments sort was successful
  if (adjustmentsSortResult > 0) {
    return 1;
  } else if (adjustmentsSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortAsc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Villas's adjustments and room type are identical
  return 0;
}

/**
 * Sort function to sort villas based on Adjustments points cost DESCENDING
 *
 * The sort priorities are the following:
 *  1. Adjustments DESC (highest to lowest)
 *  2. Room type alphabetical ASC (A-Z)
 */
function adjustmentsSortDescFunc(a: any, b:any) {
  // Try to sort by Adjustments
  const adjustmentsSortResult = evalAdjustmentsSortDesc(
    a[SORT_FIELDS[SortType.ADJUSTMENTS]],
    b[SORT_FIELDS[SortType.ADJUSTMENTS]]
  );

  // Adjustments sort was successful
  if (adjustmentsSortResult > 0) {
    return 1;
  } else if (adjustmentsSortResult < 0) {
    return -1;
  }

  // Try to sort by Room Type alphabetical
  const roomTypeAlphaSortResult = evalRoomTypeAlphaSortAsc(
    a[SORT_FIELDS[SortType.ROOM]],
    b[SORT_FIELDS[SortType.ROOM]]
  );

  // Room Type alphabetical sort was successful
  if (roomTypeAlphaSortResult > 0) {
    return 1;
  } else if (roomTypeAlphaSortResult < 0) {
    return -1;
  }

  // Villas's adjustments and room type are identical
  return 0;
}

/**
 * sort villas
 *
 * If a sort icon was clicked, the switch logic is used for the sort field
 * that was clicked. If it's the initial load, sortField and
 * sortDirection will not be passed as params, and the default case will be
 * used in the switch.
 */
function sortVillas(
  roomsSortOrder: any,
  villas: DailyAvailability[],
  sortField?: SortType,
  sortDirection?: SortDirection
): any[] {
  let sortFunction:any
  switch (sortField) {
    // Sort by Room
    case SortType.ROOM:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Room ASC
        sortFunction = (a:any, b:any) => roomSortAscFunc(a, b, roomsSortOrder);
      } else {
        // Sort by Room DESC
        sortFunction = (a:any, b:any) => roomSortDescFunc(a, b, roomsSortOrder);
      }
      break;
    // Sort by View
    case SortType.VIEW:
      if (sortDirection === SortDirection.ASC) {
        // Sort by View ASC
        sortFunction = (a:any, b:any) => viewSortAscFunc(a, b);
      } else {
        // Sort by View DESC
        sortFunction = (a:any, b:any) => viewSortDescFunc(a, b);
      }
      break;
    // Sort by Points
    case SortType.POINTS:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Points ASC
        sortFunction = (a:any, b:any) => pointsSortAscFunc(a, b);
      } else {
        // Sort by Points DESC
        sortFunction = (a:any, b:any) => pointsSortDescFunc(a, b);
      }
      break;
    // Sort by Adjustments
    case SortType.ADJUSTMENTS:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Adjustments ASC
        sortFunction = (a:any, b:any) => adjustmentsSortAscFunc(a, b);
      } else {
        // Sort by Adjustments DESC
        sortFunction = (a:any, b:any) => adjustmentsSortDescFunc(a, b);
      }
      break;

    // default sort
    default:
      // sortFunction = (a:any, b:any) => contractIdSortAscFunc(a, b);
  }

  // Return the sorted contracts
  return villas.sort(sortFunction);
}

function sortVillasUnavailable(
  roomsSortOrder: any,
  villas: WaitlistResult[],
  sortField?: SortType,
  sortDirection?: SortDirection
): any[] {
  let sortFunction:any
  switch (sortField) {
    // Sort by Room
    case SortType.ROOM:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Room ASC
        sortFunction = (a:any, b:any) => roomSortAscFunc(a, b, roomsSortOrder);
      } else {
        // Sort by Room DESC
        sortFunction = (a:any, b:any) => roomSortDescFunc(a, b, roomsSortOrder);
      }
      break;
    // Sort by View
    case SortType.VIEW:
      if (sortDirection === SortDirection.ASC) {
        // Sort by View ASC
        sortFunction = (a:any, b:any) => viewSortAscFunc(a, b);
      } else {
        // Sort by View DESC
        sortFunction = (a:any, b:any) => viewSortDescFunc(a, b);
      }
      break;
    // Sort by Points
    case SortType.POINTS:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Points ASC
        sortFunction = (a:any, b:any) => pointsSortAscFunc(a, b);
      } else {
        // Sort by Points DESC
        sortFunction = (a:any, b:any) => pointsSortDescFunc(a, b);
      }
      break;
    // Sort by Adjustments
    case SortType.ADJUSTMENTS:
      if (sortDirection === SortDirection.ASC) {
        // Sort by Adjustments ASC
        sortFunction = (a:any, b:any) => adjustmentsSortAscFunc(a, b);
      } else {
        // Sort by Adjustments DESC
        sortFunction = (a:any, b:any) => adjustmentsSortDescFunc(a, b);
      }
      break;

    // default sort
    default:
      // sortFunction = (a:any, b:any) => contractIdSortAscFunc(a, b);
  }

  // Return the sorted contracts
  return villas.sort(sortFunction);
}

function compareVillas(_arr1: WaitlistResult[], _arr2: WaitlistResult[], _arrSortOrder: any) {
  if (
    !Array.isArray(_arr1)
    || !Array.isArray(_arr2)
    || _arr1.length !== _arr2.length
    ) {
      return false;
    }
  
  // .concat() to not mutate arguments
  let arrSortFunction = (a:any, b:any) => roomSortAscFunc(a, b, _arrSortOrder);
  const arr1 = _arr1.concat().sort(arrSortFunction);
  const arr2 = _arr2.concat().sort(arrSortFunction);
  
  for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
          return false;
       }
  }
  
  return true;
}

export {
  sortVillas,
  SortType,
  SortDirection,
  sortVillasUnavailable,
  compareVillas
};
